import type { ReactNode } from "react";

import { SafeHtml } from "~/hec/components/atoms/safe-html/safe-html";
import { TelehealthGetStartedButton } from "~/hec/telehealth/components/get-started-button";
import type { C_ProductInfo } from "~/lib/app.types";
import type { SerializeFrom } from "~/lib/types";

import { PDP_CTA_CONTEXT } from "./constants";
import type { Glp1BuyColumnContents } from "./pricing-regions";
import { TelehealthValueProps } from "./telehealth-value-props";

export interface TelehealthInfoColumnProps {
  product: SerializeFrom<C_ProductInfo>;
  /**
   * Site identifier for the value-prop copy ("FSA" / "HSA" / "FSA/HSA"), or
   * `null` on a site with no eligibility label. Required — see
   * {@link TelehealthValueProps}.
   */
  siteLabel: string | null;
  /**
   * Treatment slug the Get Started CTA starts, resolved from the product's
   * `c_th_condition_type`. `undefined` ⇒ no CTA (fail-closed): an Rx product
   * whose condition we can't identify must not offer a purchase path at all.
   */
  treatmentSlug?: string;
  /**
   * Anchor for the "How it Works" jump link. Omitted when this product's
   * `pdp-glp-1-pricing` entry authors no below-the-fold content, so the link
   * can't point at an anchor that isn't on the page — or, worse, at unrelated
   * content that happens to occupy the below-fold stack.
   */
  howItWorksHref?: string;
  /** Title + brand row, rendered by the route (shared with the standard column). */
  titleBrand?: ReactNode;
  /** PowerReviews-synced rating row, rendered by the route. */
  ratings?: ReactNode;
  /** Price block — composed by the route inside the VariantSelector context. */
  price?: ReactNode;
  /**
   * Authored Builder pricing section (`pdp-glp-1-pricing`). Rendered in place of
   * the coded price + Get Started pair — the same supersede rule
   * `pdp-inline-content` uses over BM feature content — because the author is
   * expected to compose both (a `GLP1Pricing` block and a
   * `TelehealthFirstQuestion` in generic mode).
   */
  pricingSection?: ReactNode;
  /**
   * Which halves the authored section actually supplies, from
   * `describeGlp1BuyColumn`. Each half it does NOT supply falls back to the
   * coded one, so the supersede has a FLOOR: an entry carrying a price but no
   * CTA keeps the coded Get Started rather than leaving a live Rx PDP with no
   * path forward — the treatment template has no Add-to-Cart to fall back on.
   *
   * Omitted (or with `pricingSection` absent) ⇒ both coded halves render, which
   * is what keeps a medication from losing its buy column waiting on content.
   */
  pricingSectionProvides?: Glp1BuyColumnContents;
  /** Segment / CIO trackers. Render null; kept firing on telehealth PDPs. */
  analytics?: ReactNode;
}

/**
 * ET-216 — the telehealth (GLP-1) treatment info column, per XD-149 node
 * 3298-17450 and the ET-213 element list.
 *
 * The structural twin of the LMN column in `product-details.tsx`: an early
 * return that replaces the transactional block wholesale. What it drops by
 * simply not rendering it — quantity stepper, Add to Cart, cross-sell, variant
 * options, promotion callouts, compare-to and cross-promo messaging — is the
 * point: these are prescription products whose only path forward is intake.
 * "Read More" goes too (ET-213); the long description is in the page's
 * "Product Description" accordion below.
 *
 * Anything needing React context comes in as a slot (title, ratings, price,
 * trackers) so this renders standalone in a unit test — no VariantSelector, no
 * PowerReviews SDK, no site context. Plain product fields it reads directly.
 */
export function TelehealthInfoColumn({
  product,
  siteLabel,
  treatmentSlug,
  howItWorksHref,
  titleBrand,
  ratings,
  price,
  pricingSection,
  pricingSectionProvides,
  analytics,
}: TelehealthInfoColumnProps) {
  const p = product as Record<string, unknown>;
  // Short description only — the long one is the page's "Product Description"
  // accordion (ET-509). `||`, not `??`: an EMPTY `c_siteShortDescription` must
  // fall through to `shortDescription`, matching the standard column's
  // truthiness chain.
  const description =
    (p.c_siteShortDescription as string | undefined) ||
    product.shortDescription ||
    undefined;

  // A half is superseded only when an authored section is actually rendering
  // AND it contributes that half. Both default to "not provided", so a caller
  // that passes no `pricingSectionProvides` gets the coded pair — fail-open on
  // the buy column, which is the only safe direction on an Rx PDP.
  const authoredPrice =
    Boolean(pricingSection) && !!pricingSectionProvides?.price;
  const authoredCta = Boolean(pricingSection) && !!pricingSectionProvides?.cta;

  return (
    <>
      <div
        className="relative flex w-full flex-col gap-3 px-5 sm:px-0"
        data-testid="pdp-telehealth-section"
      >
        <div className="space-y-3">
          {/* Title + brand — hoisted above the image on mobile by the route, in
              column at md+ (same split as the standard column). On a treatment
              PDP this node also carries the eligibility chip ABOVE the product
              name, so the chip follows the title through both placements
              instead of sitting further down the column. */}
          <div className="min-[768px]:flex min-[768px]:flex-col min-[768px]:gap-3 hidden">
            {titleBrand}
          </div>

          {product.id && (
            <p className="text-xs text-gray-500" data-testid="pdp-sku">
              SKU# {product.id}
            </p>
          )}

          {ratings}

          {/* Short description only; no "Read More" (ET-213). */}
          {description && (
            <SafeHtml
              html={description}
              className="prose prose-sm max-w-none font-grotesque text-[0.9625rem] font-light leading-[1.58] text-foreground"
            />
          )}
        </div>

        {/* Per-half supersede, not all-or-nothing: see
            `pricingSectionProvides`. Order is always price → CTA, with the
            authored region standing in for whichever halves it supplies, so a
            CTA-only entry doesn't end up with the coded price BELOW the button.
            `pt-6` lands on whichever of the three renders first — the top
            padding belongs to the block, not to the price specifically. */}
        {!authoredPrice && <div className="pt-6">{price}</div>}

        {pricingSection && (
          <div
            className={authoredPrice ? "pt-6" : "mt-4"}
            data-testid="pdp-glp1-pricing-section"
          >
            {pricingSection}
          </div>
        )}

        {!authoredCta && treatmentSlug && (
          <div className="mt-4 flex flex-col items-start gap-2">
            <TelehealthGetStartedButton
              treatment={treatmentSlug}
              source={PDP_CTA_CONTEXT.source}
              ctaLocation={PDP_CTA_CONTEXT.ctaLocation}
              data-testid="pdp-get-started-button"
            />
          </div>
        )}

        {howItWorksHref && (
          <a
            href={howItWorksHref}
            className="mt-2 font-grotesque text-sm font-normal text-foreground underline"
            data-testid="pdp-how-it-works-link"
          >
            How it Works
          </a>
        )}

        <TelehealthValueProps siteLabel={siteLabel} className="mt-4" />
      </div>

      {analytics}
    </>
  );
}

/**
 * Streaming placeholder for the column above. Deliberately NOT the shared
 * `OptimisticInfoColumn`/`InfoColumnSkeleton`: those seed a price + variant +
 * Add-to-Cart layout, which would paint a purchase affordance on a prescription
 * PDP for the length of one product fetch.
 */
export function TelehealthInfoColumnSkeleton() {
  return (
    <div
      className="relative flex w-full animate-pulse flex-col gap-3 px-5 sm:px-0"
      data-testid="pdp-telehealth-loading"
    >
      <div className="h-8 w-3/4 rounded bg-gray-01" />
      <div className="h-4 w-1/3 rounded bg-gray-01" />
      <div className="h-3 w-1/4 rounded bg-gray-01" />
      <div className="h-12 w-full rounded bg-gray-01" />
      <div className="mt-6 h-8 w-1/3 rounded bg-gray-01" />
      <div className="min-[768px]:w-[200px] mt-4 h-[54px] w-full rounded-[.5rem] bg-gray-01" />
      <div className="mt-4 h-14 w-full rounded bg-gray-01" />
    </div>
  );
}
